import { t } from "i18next"
import { localisedValue } from "utils"

const BOOLEAN_FIELD_TYPES = ["checkbox", "toggle"]
const EMPTY_STRINGS = new Set(["", "null", "undefined", "[]"])

const isEmptyValue = value => {
  if (value == null) return true
  if (Array.isArray(value)) return !value.length
  return EMPTY_STRINGS.has(String(value).trim().toLowerCase())
}

const displayOrDash = value => (isEmptyValue(value) ? "-" : value)

const resolveLabel = option => {
  if (!option || typeof option !== "object") return null
  return localisedValue({ object: option, key: "label" }) || option?.label || null
}

const resolveListLabels = (options, { withScore = false } = {}) => {
  if (!Array.isArray(options) || !options.length) return []
  return options.reduce((result, option) => {
    const label = resolveLabel(option)
    if (label) result.push(withScore ? { label, score: option?.score } : label)
    return result
  }, [])
}

const getDisplayValue = question => {
  const { fieldType, valueType, resolvedValue } = question || {}
  const value = localisedValue({ object: question, key: "value" }) ?? question?.value

  if (BOOLEAN_FIELD_TYPES.includes(fieldType) || valueType === "boolean") {
    if (value === true || value === "true") return t("Yes")
    if (value === false || value === "false") return t("No")
    return displayOrDash(value)
  }

  if (resolvedValue != null && !Array.isArray(resolvedValue)) {
    return resolveLabel(resolvedValue) || displayOrDash(value)
  }

  return displayOrDash(value)
}

const transformQuestion = question => ({
  ...question,
  displayValue: getDisplayValue(question),
  displayTags: resolveListLabels(question?.resolvedValue),
  displayScaleEntries: resolveListLabels(question?.resolvedValue, { withScore: true }),
})

const extractEntityValidityInfo = payload => {
  const entityType = payload?.entityType
  for (const [sectionKey, questions] of Object.entries(payload?.sections || {})) {
    const match = (questions || []).find(q => q?.questionKey === "is_entity_valid")
    if (match) {
      return {
        sectionKey,
        entityType,
        isValid: match?.value === true || match?.value === "true",
      }
    }
  }
  return null
}

export const transformReviewDetailData = payload => {
  if (!payload) return payload

  const sections = Object.entries(payload?.sections || {}).map(([key, questions]) => [
    key,
    (questions || []).map(transformQuestion),
  ])

  return {
    ...payload,
    entityValidityInfo: extractEntityValidityInfo(payload),
    sections: Object.fromEntries(sections),
  }
}

const reviewDetailTransformHelper = response => ({
  ...response,
  data: transformReviewDetailData(response?.data),
})

export default reviewDetailTransformHelper
